import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEntriesStore, useSettingsStore } from '../stores';
import { Editor, LocationPicker } from '../components/entries';
import { Button, Input, Alert } from '../components/ui';
import { api } from '../lib/api';

const NEW_ENTRY_DRAFT_KEY = 'caderno:new-entry-draft';

interface NewEntryDraft {
  title: string;
  content: string;
  tags: string[];
  location: { latitude: number; longitude: number; locationName: string } | null;
}

function loadNewEntryDraft(): NewEntryDraft | null {
  try {
    const raw = localStorage.getItem(NEW_ENTRY_DRAFT_KEY);
    return raw ? (JSON.parse(raw) as NewEntryDraft) : null;
  } catch {
    return null;
  }
}

function clearNewEntryDraft() {
  localStorage.removeItem(NEW_ENTRY_DRAFT_KEY);
}

export function EntryEditPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { preferences } = useSettingsStore();
  const {
    currentEntry,
    isLoading,
    error,
    fetchEntry,
    createEntry,
    updateEntry,
    deleteEntry,
    clearCurrentEntry,
    clearError,
    tags: existingTags,
    fetchTags,
  } = useEntriesStore();

  const isNew = !id;

  // Lazy-initialized so a restored draft is present on the very first render —
  // the Editor below is uncontrolled and only reads its initialContent prop once, on mount.
  const [title, setTitle] = useState(() => (isNew ? loadNewEntryDraft()?.title ?? '' : ''));
  const [content, setContent] = useState(() => (isNew ? loadNewEntryDraft()?.content ?? '' : ''));
  const [tags, setTags] = useState<string[]>(() => (isNew ? loadNewEntryDraft()?.tags ?? [] : []));
  const [tagInput, setTagInput] = useState('');
  const [showTagSuggestions, setShowTagSuggestions] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [location, setLocation] = useState<{
    latitude: number; longitude: number; locationName: string;
  } | null>(() => (isNew ? loadNewEntryDraft()?.location ?? null : null));
  const [showLocationPicker, setShowLocationPicker] = useState(false);

  // Tracks images queued locally (blob URL → File) — uploaded on save, never before
  const pendingUploads = useRef<Map<string, File>>(new Map());

  // Once the draft is cleared (save/cancel), stop persisting — otherwise the
  // currentEntry-sync effect below can re-populate state post-save and the
  // persist effect would write the draft right back to localStorage.
  const draftClearedRef = useRef(false);

  // Revoke any unreleased blob URLs when the page unmounts
  useEffect(() => {
    return () => {
      for (const blobUrl of pendingUploads.current.keys()) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, []);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    if (id) {
      fetchEntry(id);
    }

    return () => {
      clearCurrentEntry();
      clearError();
    };
  }, [id, fetchEntry, clearCurrentEntry, clearError]);

  // Persist the in-progress draft for new entries only; cleared on save or cancel
  useEffect(() => {
    if (!isNew || draftClearedRef.current) return;
    localStorage.setItem(
      NEW_ENTRY_DRAFT_KEY,
      JSON.stringify({ title, content, tags, location })
    );
  }, [isNew, title, content, tags, location]);

  useEffect(() => {
    if (currentEntry) {
      setTitle(currentEntry.title);
      setContent(currentEntry.content);
      setTags(currentEntry.tags);
      if (currentEntry.locationLatitude != null && currentEntry.locationLongitude != null) {
        setLocation({
          latitude: currentEntry.locationLatitude,
          longitude: currentEntry.locationLongitude,
          locationName: currentEntry.locationName ?? '',
        });
      }
    }
  }, [currentEntry]);

  const handleEditorChange = useCallback(
    (newContent: string) => {
      setContent(newContent);
    },
    []
  );

  const handleImageQueued = useCallback((blobUrl: string, file: File) => {
    pendingUploads.current.set(blobUrl, file);
  }, []);

  // Upload all pending images for entryId, returns content with blob URLs swapped for real URLs.
  // Images removed from the editor before saving are skipped (blob URL not in content).
  const uploadPendingImages = useCallback(async (entryId: string, rawContent: string): Promise<string> => {
    if (pendingUploads.current.size === 0) return rawContent;
    let resolved = rawContent;
    for (const [blobUrl, file] of Array.from(pendingUploads.current.entries())) {
      if (!rawContent.includes(blobUrl)) {
        URL.revokeObjectURL(blobUrl);
        continue;
      }
      const result = await api.uploadImage(entryId, file);
      resolved = resolved.replaceAll(blobUrl, result.url);
      URL.revokeObjectURL(blobUrl);
    }
    pendingUploads.current.clear();
    return resolved;
  }, []);

  const handleAddTag = (tagToAdd?: string) => {
    const tag = (tagToAdd ?? tagInput).trim().toLowerCase();
    if (tag && !tags.includes(tag) && tags.length < 20) {
      setTags([...tags, tag]);
      setTagInput('');
      setShowTagSuggestions(false);
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const tagSuggestions = tagInput.trim()
    ? existingTags.filter(
        (tag) =>
          tag.toLowerCase().includes(tagInput.trim().toLowerCase()) && !tags.includes(tag)
      )
    : [];

  const handleSave = async () => {
    if (!title.trim()) return;

    setIsSaving(true);
    try {
      if (isNew) {
        // Create with empty content first — we need the ID to upload images
        const entry = await createEntry({
          title: title.trim(),
          content: content,
          tags,
          locationLatitude: location?.latitude,
          locationLongitude: location?.longitude,
          locationName: location?.locationName,
        });
        const finalContent = await uploadPendingImages(entry.id, content);
        await updateEntry(entry.id, {
          title: title.trim(),
          content: finalContent,
          tags,
          locationLatitude: location?.latitude,
          locationLongitude: location?.longitude,
          locationName: location?.locationName,
        });
        draftClearedRef.current = true;
        clearNewEntryDraft();
        navigate(`/entries/${entry.id}/view`, { replace: true });
      } else if (id) {
        const finalContent = await uploadPendingImages(id, content);
        await updateEntry(id, {
          title: title.trim(),
          content: finalContent,
          tags,
          locationLatitude: location?.latitude,
          locationLongitude: location?.longitude,
          locationName: location?.locationName,
        });
        navigate(`/entries/${id}/view`);
      }
    } catch {
      // Error handled by store
    } finally {
      setIsSaving(false);
    }
  };

  const hasUnsavedChanges = () => {
    if (isNew) {
      return Boolean(title.trim() || content.trim() || tags.length > 0 || location);
    }
    if (!currentEntry) return false;
    const savedLocation =
      currentEntry.locationLatitude != null && currentEntry.locationLongitude != null
        ? {
            latitude: currentEntry.locationLatitude,
            longitude: currentEntry.locationLongitude,
            locationName: currentEntry.locationName ?? '',
          }
        : null;
    return (
      title !== currentEntry.title ||
      content !== currentEntry.content ||
      tags.length !== currentEntry.tags.length ||
      tags.some((tag, i) => tag !== currentEntry.tags[i]) ||
      JSON.stringify(location) !== JSON.stringify(savedLocation)
    );
  };

  const leaveWithoutSaving = () => {
    if (isNew) {
      draftClearedRef.current = true;
      clearNewEntryDraft();
    }
    navigate(isNew ? '/entries' : `/entries/${id}/view`);
  };

  const handleCancel = () => {
    if (hasUnsavedChanges()) {
      setShowCancelConfirm(true);
    } else {
      leaveWithoutSaving();
    }
  };

  const handleDelete = async () => {
    if (!id || isNew) return;

    try {
      await deleteEntry(id);
      navigate('/entries', { replace: true });
    } catch {
      // Error handled by store
    }
  };

  if (isLoading && !isNew) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-ink">
          {isNew ? t('entries.newEntry') : t('entries.editEntry')}
        </h1>

        <div className="flex gap-2">
          <Button variant="ghost" onClick={handleCancel}>
            {t('common.cancel')}
          </Button>
          {!isNew && (
            <Button
              variant="danger"
              onClick={() => setShowDeleteConfirm(true)}
            >
              {t('common.delete')}
            </Button>
          )}
          <Button onClick={handleSave} isLoading={isSaving} disabled={!title.trim()}>
            {t('common.save')}
          </Button>
        </div>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="space-y-4">
        <Input
          placeholder={t('entries.titlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="text-xl font-semibold"
        />

        {(isNew || currentEntry) && (
          <Editor
            key={id ?? 'new'}
            initialContent={isNew ? content : currentEntry?.content}
            onChange={handleEditorChange}
            placeholder={t('entries.contentPlaceholder')}
            fontSize={preferences.editorFontSize}
            onImageQueued={handleImageQueued}
            onLocationClick={() => setShowLocationPicker(true)}
          />
        )}

        {location && (
          <div className="flex items-center gap-1.5 text-sm text-ink-500">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
            <span>
              {location.locationName || `${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`}
            </span>
            <button
              onClick={() => setLocation(null)}
              className="ml-1 text-ink-400 hover:text-ink transition-colors"
              aria-label="Remove location"
            >
              &times;
            </button>
          </div>
        )}

        <div className="space-y-2">
          <label className="label">{t('entries.tags')}</label>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center gap-1 rounded-sm bg-paper-200 px-3 py-1 text-sm text-ink-600"
              >
                {tag}
                <button
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-1 text-ink-400 hover:text-ink transition-colors"
                >
                  &times;
                </button>
              </span>
            ))}
          </div>
          <div className="relative flex gap-2">
            <div className="relative flex-1">
              <Input
                placeholder={t('entries.addTag')}
                value={tagInput}
                onChange={(e) => {
                  setTagInput(e.target.value);
                  setShowTagSuggestions(true);
                }}
                onFocus={() => setShowTagSuggestions(true)}
                onBlur={() => setTimeout(() => setShowTagSuggestions(false), 100)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                className="w-full"
              />
              {showTagSuggestions && tagSuggestions.length > 0 && (
                <ul className="absolute top-full left-0 z-20 mt-1 max-h-48 w-full overflow-y-auto rounded border border-paper-300 bg-paper-50 shadow-sm">
                  {tagSuggestions.map((tag) => (
                    <li key={tag}>
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => handleAddTag(tag)}
                        className="block w-full px-3 py-1.5 text-left text-sm text-ink-600 hover:bg-paper-200"
                      >
                        {tag}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <Button variant="secondary" onClick={() => handleAddTag()}>
              {t('common.add')}
            </Button>
          </div>
        </div>

      </div>

      {showLocationPicker && (
        <LocationPicker
          initialLocation={location ?? undefined}
          referenceDate={currentEntry?.entryDate}
          onSave={(loc) => { setLocation(loc); setShowLocationPicker(false); }}
          onCancel={() => setShowLocationPicker(false)}
        />
      )}

      {/* Cancel confirmation modal */}
      {showCancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded border border-paper-300 bg-paper-50 p-6">
            <h3 className="text-base font-semibold text-ink">
              {t('entries.discardConfirm')}
            </h3>
            <p className="mt-2 text-sm text-ink-500">
              {t('entries.discardWarning')}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowCancelConfirm(false)}>
                {t('entries.keepEditing')}
              </Button>
              <Button variant="danger" onClick={leaveWithoutSaving}>
                {t('entries.discard')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded border border-paper-300 bg-paper-50 p-6">
            <h3 className="text-base font-semibold text-ink">
              {t('entries.deleteConfirm')}
            </h3>
            <p className="mt-2 text-sm text-ink-500">
              {t('entries.deleteWarning')}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
                {t('common.cancel')}
              </Button>
              <Button variant="danger" onClick={handleDelete}>
                {t('common.delete')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
